/**
 * URL Validation and Security for Plugins
 *
 * This module provides URL validation, whitelisting, and blacklisting
 * functionality to ensure plugins can only navigate to safe, approved domains.
 */

// Social Media Platforms
const SOCIAL_MEDIA_DOMAINS = [
  "facebook.com",
  "instagram.com",
  "twitter.com",
  "x.com",
  "linkedin.com",
  "tiktok.com",
  "snapchat.com",
  "youtube.com",
  "youtu.be",
  "pinterest.com",
  "reddit.com",
  "discord.com",
  "telegram.org",
  "whatsapp.com",
  "signal.org",
]

// Payment and Financial Services
const PAYMENT_DOMAINS = [
  "venmo.com",
  "cashapp.com",
  "paypal.com",
  "zelle.com",
  "apple.com", // Apple Pay
  "google.com", // Google Pay
  "stripe.com",
  "square.com",
  "splitwise.com",
]

// Communication and Collaboration
const COMMUNICATION_DOMAINS = [
  "zoom.us",
  "meet.google.com",
  "teams.microsoft.com",
  "slack.com",
  "notion.so",
  "airtable.com",
  "trello.com",
  "asana.com",
  "monday.com",
]

// Media and Content Sharing
const MEDIA_DOMAINS = [
  "drive.google.com",
  "dropbox.com",
  "icloud.com",
  "onedrive.live.com",
  "box.com",
  "imgur.com",
  "flickr.com",
  "vimeo.com",
  "soundcloud.com",
  "spotify.com",
  "apple.com", // Apple Music (already in payments but that's ok)
]

// Maps and Location Services
const MAPS_DOMAINS = [
  "maps.google.com",
  "maps.apple.com",
  "waze.com",
  "mapquest.com",
  "bing.com", // Bing Maps
]

// Event and Ticketing Platforms
const EVENT_DOMAINS = [
  "eventbrite.com",
  "meetup.com",
  "facebook.com", // Facebook Events (already in social)
  "ticketmaster.com",
  "stubhub.com",
  "seatgeek.com",
  "rsvp.com",
]

// Food and Delivery
const FOOD_DOMAINS = [
  "doordash.com",
  "ubereats.com",
  "grubhub.com",
  "postmates.com",
  "seamless.com",
  "opentable.com",
  "yelp.com",
  "zomato.com",
]

// Shopping and E-commerce (Major trusted platforms only)
const SHOPPING_DOMAINS = [
  "amazon.com",
  "target.com",
  "walmart.com",
  "costco.com",
  "bestbuy.com",
  "etsy.com",
  "ebay.com",
]

// Educational and Reference
const EDUCATIONAL_DOMAINS = [
  "wikipedia.org",
  "github.com",
  "stackoverflow.com",
  "medium.com",
  "substack.com",
  "coursera.org",
  "udemy.com",
  "khanacademy.org",
]

// News and Media (Major trusted sources)
const NEWS_DOMAINS = [
  "nytimes.com",
  "washingtonpost.com",
  "bbc.com",
  "cnn.com",
  "reuters.com",
  "npr.org",
  "apnews.com",
]

// Combine all whitelisted domains
const WHITELISTED_DOMAINS = [
  ...SOCIAL_MEDIA_DOMAINS,
  ...PAYMENT_DOMAINS,
  ...COMMUNICATION_DOMAINS,
  ...MEDIA_DOMAINS,
  ...MAPS_DOMAINS,
  ...EVENT_DOMAINS,
  ...FOOD_DOMAINS,
  ...SHOPPING_DOMAINS,
  ...EDUCATIONAL_DOMAINS,
  ...NEWS_DOMAINS,
]

// Blacklisted domains (known malicious or inappropriate)
const BLACKLISTED_DOMAINS = [
  // Add known malicious domains here
  // This list should be maintained and updated regularly
  "bit.ly", // URL shorteners can be risky
  "tinyurl.com",
  "goo.gl",
  "t.co", // Twitter shortener - might be too restrictive
]

// URL schemes that are allowed
const ALLOWED_SCHEMES = ["http:", "https:", "mailto:", "tel:"]

export interface UrlValidationResult {
  isValid: boolean
  isAllowed: boolean
  reason?: string
  category?: string
  domain?: string
}

/**
 * Validates a URL for security and policy compliance
 */
export function validateUrl(url: string): UrlValidationResult {
  try {
    // Normalize URL - add https:// if no protocol is provided
    let normalizedUrl = url.trim()
    if (normalizedUrl && !normalizedUrl.match(/^[a-zA-Z][a-zA-Z\d+\-.]*:/)) {
      normalizedUrl = "https://" + normalizedUrl
    }

    // Basic URL validation
    const urlObj = new URL(normalizedUrl)

    // Check if scheme is allowed
    if (!ALLOWED_SCHEMES.includes(urlObj.protocol)) {
      return {
        isValid: false,
        isAllowed: false,
        reason: `Protocol "${urlObj.protocol}" is not allowed. Allowed protocols: ${ALLOWED_SCHEMES.join(", ")}`,
      }
    }

    // For non-HTTP protocols, allow them (mailto, tel)
    if (urlObj.protocol !== "http:" && urlObj.protocol !== "https:") {
      return {
        isValid: true,
        isAllowed: true,
        category: "communication",
      }
    }

    const domain = urlObj.hostname.toLowerCase()

    // Remove www. prefix for comparison
    const normalizedDomain = domain.replace(/^www\./, "")

    // Check blacklist first
    const isBlacklisted = BLACKLISTED_DOMAINS.some(
      (blacklistedDomain) =>
        normalizedDomain === blacklistedDomain ||
        normalizedDomain.endsWith("." + blacklistedDomain),
    )

    if (isBlacklisted) {
      return {
        isValid: true,
        isAllowed: false,
        reason: `Domain "${domain}" is blacklisted for security reasons`,
        domain: normalizedDomain,
      }
    }

    // Check whitelist
    const isWhitelisted = WHITELISTED_DOMAINS.some(
      (whitelistedDomain) =>
        normalizedDomain === whitelistedDomain ||
        normalizedDomain.endsWith("." + whitelistedDomain),
    )

    if (isWhitelisted) {
      // Determine category
      let category = "general"
      if (SOCIAL_MEDIA_DOMAINS.includes(normalizedDomain))
        category = "social-media"
      else if (PAYMENT_DOMAINS.includes(normalizedDomain)) category = "payment"
      else if (COMMUNICATION_DOMAINS.includes(normalizedDomain))
        category = "communication"
      else if (MEDIA_DOMAINS.includes(normalizedDomain)) category = "media"
      else if (MAPS_DOMAINS.includes(normalizedDomain)) category = "maps"
      else if (EVENT_DOMAINS.includes(normalizedDomain)) category = "events"
      else if (FOOD_DOMAINS.includes(normalizedDomain)) category = "food"
      else if (SHOPPING_DOMAINS.includes(normalizedDomain))
        category = "shopping"
      else if (EDUCATIONAL_DOMAINS.includes(normalizedDomain))
        category = "educational"
      else if (NEWS_DOMAINS.includes(normalizedDomain)) category = "news"

      return {
        isValid: true,
        isAllowed: true,
        category,
        domain: normalizedDomain,
      }
    }

    // Not whitelisted
    return {
      isValid: true,
      isAllowed: false,
      reason: `Domain "${domain}" is not in the approved whitelist. Only trusted domains are allowed for security.`,
      domain: normalizedDomain,
    }
  } catch (error) {
    return {
      isValid: false,
      isAllowed: false,
      reason: `Invalid URL format: ${error instanceof Error ? error.message : "Unknown error"}`,
    }
  }
}

/**
 * Gets a user-friendly category name
 */
export function getCategoryDisplayName(category: string): string {
  const categoryNames = {
    "social-media": "Social Media",
    payment: "Payment & Finance",
    communication: "Communication",
    media: "Media & Files",
    maps: "Maps & Location",
    events: "Events & Tickets",
    food: "Food & Delivery",
    shopping: "Shopping",
    educational: "Educational",
    news: "News & Media",
    general: "General",
  }

  return categoryNames[category] || "Unknown"
}

/**
 * Gets all whitelisted domains organized by category (for debugging/admin)
 */
export function getWhitelistedDomainsByCategory() {
  return {
    "Social Media": SOCIAL_MEDIA_DOMAINS,
    "Payment & Finance": PAYMENT_DOMAINS,
    Communication: COMMUNICATION_DOMAINS,
    "Media & Files": MEDIA_DOMAINS,
    "Maps & Location": MAPS_DOMAINS,
    "Events & Tickets": EVENT_DOMAINS,
    "Food & Delivery": FOOD_DOMAINS,
    Shopping: SHOPPING_DOMAINS,
    Educational: EDUCATIONAL_DOMAINS,
    "News & Media": NEWS_DOMAINS,
  }
}
